import React, { useContext, useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import { Tab as LinkTab } from '@mui/material'
import { removeRefreshToken, removeToken } from '../useToken'
import useRole from '../useUserRole'
import { AppSettingsContext } from '../App'

import {
  Logo,
  LogOutTab,
  NavigationHeadPaper,
  NavigationLogoLink,
  NavigationTabs,
} from '../styles/components/NavigationStyle'

import logo from '../images/logo_white.png'
import { removeSessionFilters } from '../useSessionFilters'

const Navigation = () => {
  const [activeTab, setActiveTab] = useState('/statistics')
  const [activeUserRole, setActiveUserRole] = useState('')
  const { userRole, removeUserRole } = useRole()
  const { isLoggedIn, setIsLoggedIn } = useContext(AppSettingsContext)

  const navigate = useNavigate()
  const location = useLocation()

  const getAvailableTabsByRole = (role) => {
    const tabs = ['/profile', 'logout']

    if (role === 'admin' || role === 'viewer' || role === 'customer') {
      tabs.unshift('/statistics')
    }
    if (role === 'admin' || role === 'customer') {
      tabs.unshift('/scan')
      tabs.unshift('/table')
    }
    if (role === 'admin') {
      tabs.unshift('/users')
    }

    return tabs
  }

  const getDefaultRouteByRole = (role) => {
    if (role === 'admin') {
      return '/table'
    }
    if (role === 'viewer' || role === 'customer') {
      return '/statistics'
    }

    return '/profile'
  }

  const handleTabClick = (event, clickedTab) => {
    setActiveTab(clickedTab || getDefaultRouteByRole(activeUserRole))
  }

  const handleLogOut = () => {
    removeToken()
    removeRefreshToken()
    removeUserRole()
    sessionStorage.removeItem('username')
    removeSessionFilters()
    setIsLoggedIn(false)
    navigate('/login')
  }

  useEffect(() => {
    const loadedUserRole = userRole ? userRole : sessionStorage.getItem('userRole')
    const availableTabs = getAvailableTabsByRole(loadedUserRole)

    setActiveUserRole(loadedUserRole)
    // Extract only root of path for navigation bar
    const primaryPath = location.pathname.split('/')[1]
    let nextActiveTab

    switch (primaryPath) {
      case 'statistics':
        nextActiveTab = '/statistics'
        break
      case 'table':
        nextActiveTab = '/table'
        break
      case 'scan':
        nextActiveTab = '/scan'
        break
      case 'users':
        nextActiveTab = '/users'
        break
      case 'profile':
        nextActiveTab = '/profile'
        break
      default:
        nextActiveTab = getDefaultRouteByRole(loadedUserRole)
    }

    setActiveTab(
      availableTabs.includes(nextActiveTab) ? nextActiveTab : getDefaultRouteByRole(loadedUserRole)
    )
  }, [userRole, isLoggedIn, location.pathname])

  return (
    <NavigationHeadPaper className={'sticky-vertical'}>
      <NavigationLogoLink to={isLoggedIn ? getDefaultRouteByRole(activeUserRole) : '/login'} onClick={handleTabClick}>
        <Logo src={logo} alt="logo" />
      </NavigationLogoLink>
      {isLoggedIn && (
        <NavigationTabs
          value={activeTab}
          onChange={handleTabClick}
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          aria-label="navigation"
        >
          {(activeUserRole === 'admin' || activeUserRole === 'viewer' || activeUserRole === 'customer') && (
            <LinkTab
              label="Statistics"
              value="/statistics"
              component={Link}
              to="/statistics"
              aria-label="navButtonStatistics"
            />
          )}
          {(activeUserRole === 'admin' || activeUserRole === 'customer') && (
            <LinkTab label="Table" value="/table" component={Link} to="/table" aria-label="navButtonTable" />
          )}
          {(activeUserRole === 'admin' || activeUserRole === 'customer') && (
            <LinkTab label="Scan" value="/scan" component={Link} to="/scan" aria-label="navButtonScan" />
          )}
          {activeUserRole === 'admin' && (
            <LinkTab label="Users" value="/users" component={Link} to="/users" aria-label="navButtonUsers" />
          )}
          <LinkTab label="Profile" value="/profile" component={Link} to="/profile" aria-label="navButtonProfile" />
          <LogOutTab value="logout" label="Logout" onClick={handleLogOut} aria-label="logoutButton" />
        </NavigationTabs>
      )}
    </NavigationHeadPaper>
  )
}

Navigation.propTypes = {
  location: PropTypes.object,
}

export default Navigation
